/**
 * Shared import cell-error contract for Quick Add, CSV import, and Trade Capture.
 *
 * Canonical path: response.data.data.rows_with_errors (array).
 * Trade Capture may still return data.validation_errors (same array or legacy
 * object keyed by row) — both are normalized here.
 */

import { toastValidationErrors } from "helpers/toastValidationErrors";

/** Keys that are row/file-level, not a table column. */
export const IMPORT_ROW_LEVEL_ERROR_KEYS = new Set([
  "message",
  "error",
  "headers",
]);

/** Backend field aliases → TRADE_COLUMNS keys (shared across import flows). */
export const IMPORT_VALIDATION_FIELD_ALIASES = {
  ul: ["underlying", "underlying_legs", "underlying_leg"],
  commodity: ["product", "shortcut", "commodity_name"],
  contract_style: [
    "contract_style_name",
    "contract-style",
    "pcs",
    "product_contract_style",
  ],
  type: ["option_type", "structure"],
  tenor: ["style_period_tenor", "tenor_label"],
  bespoke_tenors: ["bespoke_tenor"],
  legs_strike: ["strike", "strikes", "leg_strike"],
  legs_putcall: ["put_call", "putcall"],
  legs_buysell: ["buy_sell", "buysell"],
  legs_ratio: ["ratio"],
  buy_qty: ["bid_quantity", "bid_qty"],
  sell_qty: ["ask_quantity", "ask_qty"],
  buy: ["bid"],
  sell: ["ask"],
  buy_percentage: ["buy_pct", "bid_percentage"],
  sell_percentage: ["sell_pct", "ask_percentage"],
  rf: ["risk_free"],
  cost_of_carry: ["coc"],
  actioned_at_time: ["actioned_at"],
};

export function normalizeImportErrorMessages(raw) {
  if (raw == null) return null;
  if (Array.isArray(raw)) {
    const out = raw
      .map((x) => (typeof x === "string" ? x.trim() : String(x)))
      .filter(Boolean);
    return out.length ? out : null;
  }
  if (typeof raw === "string") {
    const t = raw.trim();
    return t ? [t] : null;
  }
  return null;
}

/**
 * Normalize backend error payloads into:
 * [{ rowNumber, option, errors: { field: string[] }, rowLevelMessages: string[], rowData }]
 */
export function normalizeRowsWithErrors(raw) {
  if (!raw) return [];

  const blocks = Array.isArray(raw)
    ? raw
    : typeof raw === "object"
      ? Object.values(raw)
      : [];

  return blocks
    .filter((block) => block && typeof block === "object")
    .map((block) => {
      const errorsObj =
        block.errors && typeof block.errors === "object" ? block.errors : {};
      const errors = {};
      const rowLevelMessages = [];

      Object.entries(errorsObj).forEach(([key, value]) => {
        const msgs = normalizeImportErrorMessages(value);
        if (!msgs?.length) return;
        if (IMPORT_ROW_LEVEL_ERROR_KEYS.has(key)) {
          rowLevelMessages.push(...msgs);
        } else {
          errors[key] = msgs;
        }
      });

      const option =
        block.row_data?.option != null
          ? Number(block.row_data.option)
          : block.option != null
            ? Number(block.option)
            : block.option_number != null
              ? Number(block.option_number)
              : null;

      // Callers may hand back blocks this function already produced (the
      // modal stores them, the grid normalizes again). Keep their row number,
      // or rows without an option (Other surface ladder) lose their errors.
      const rowNumber =
        block.row_number != null
          ? Number(block.row_number)
          : block.rowNumber != null
            ? Number(block.rowNumber)
            : option;
      return {
        rowNumber,
        option: Number.isFinite(option) ? option : null,
        errors,
        rowLevelMessages: [
          ...rowLevelMessages,
          ...(Array.isArray(block.rowLevelMessages) ? block.rowLevelMessages : []),
        ],
        rowData: block.row_data || block.rowData || null,
      };
    });
}

/** Prefer rows_with_errors; fall back to validation_errors for Trade Capture. */
export function extractRowsWithErrors(response) {
  const data = response?.data?.data ?? response?.data ?? response;
  if (!data || typeof data !== "object") return [];
  const raw = data.rows_with_errors ?? data.validation_errors;
  return normalizeRowsWithErrors(raw);
}

function fieldMessagesFromErrors(errorsObj, field) {
  if (!errorsObj || !field) return null;
  const aliasList = IMPORT_VALIDATION_FIELD_ALIASES[field] ?? [];
  const tryKeys = [field, ...aliasList];
  const seen = new Set();
  for (const k of tryKeys) {
    if (!k || seen.has(k)) continue;
    seen.add(k);
    const msgs = normalizeImportErrorMessages(errorsObj[k]);
    if (msgs?.length) return msgs;
  }
  const lower = field.toLowerCase();
  for (const apiKey of Object.keys(errorsObj)) {
    if (apiKey.toLowerCase() === lower) {
      return normalizeImportErrorMessages(errorsObj[apiKey]);
    }
  }
  return null;
}

/**
 * Find the error block for a submitted row.
 * Prefer option (display #); then row_number; then 1-based table index.
 */
export function findRowErrorBlock(
  rowsWithErrors,
  { option, rowNumber, rowIndex } = {}
) {
  const list = Array.isArray(rowsWithErrors)
    ? rowsWithErrors
    : normalizeRowsWithErrors(rowsWithErrors);
  if (!list.length) return null;

  if (option != null) {
    const byOption = list.find((b) => Number(b.option) === Number(option));
    if (byOption) return byOption;
  }
  if (rowNumber != null) {
    const byRn = list.find((b) => Number(b.rowNumber) === Number(rowNumber));
    if (byRn) return byRn;
  }
  if (rowIndex != null) {
    const oneBased = rowIndex + 1;
    return (
      list.find((b) => Number(b.option) === oneBased) ||
      list.find((b) => Number(b.rowNumber) === oneBased) ||
      null
    );
  }
  return null;
}

export function getImportCellErrorMessages(
  rowsWithErrors,
  { option, rowNumber, rowIndex, field } = {}
) {
  const block = findRowErrorBlock(rowsWithErrors, {
    option,
    rowNumber,
    rowIndex,
  });
  if (!block) return null;
  return fieldMessagesFromErrors(block.errors, field);
}

export function getImportRowLevelMessages(
  rowsWithErrors,
  { option, rowNumber, rowIndex } = {}
) {
  const block = findRowErrorBlock(rowsWithErrors, {
    option,
    rowNumber,
    rowIndex,
  });
  return block?.rowLevelMessages?.length ? block.rowLevelMessages : null;
}

/**
 * Toast the top-level message and any row/file-level messages.
 * Does not parse the message to find cells — callers mark cells from
 * extractRowsWithErrors / getImportCellErrorMessages.
 */
export function toastImportValidationResponse(response, options = {}) {
  const status = response?.status;
  const topMessage = response?.data?.message;
  const rows = extractRowsWithErrors(response);
  const rowLevel = rows.flatMap((r) => r.rowLevelMessages || []);
  const parts = [topMessage, ...rowLevel].filter(Boolean);
  const message = parts.length
    ? parts.join("\n\n")
    : options.fallbackMessage || "Import failed. Please try again.";

  // Laravel request-level errors (malformed body) live at data.errors.
  const formErrors = response?.data?.errors;
  if (formErrors && typeof formErrors === "object" && !rows.length) {
    const formLines = Object.values(formErrors)
      .flat()
      .map((m) => String(m).trim())
      .filter(Boolean);
    toastValidationErrors(
      formLines.length ? formLines.join("\n") : message,
      {
        variant: status === 422 ? "warning" : "error",
        title:
          options.title ||
          (status === 422 ? "Validation Warnings" : "Validation Errors"),
        duration: options.duration ?? 5,
      }
    );
    return rows;
  }

  toastValidationErrors(message, {
    variant: status === 422 ? "warning" : "error",
    title:
      options.title ||
      (status === 422 ? "Validation Warnings" : "Validation Errors"),
    duration: options.duration ?? 5,
  });
  return rows;
}
